import React, { Suspense, useContext, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { RodRoomRequestList } from './list';
import { RodRequestDetail } from './detail';
import { ErrorBoundary, SuspenseLoader, NotFound } from '@bechtel/client-common';
import { RodRequestDetailContainerStyled, StatContainerStyled, IconContainerStyled } from './styled';
import { RodRoomStats as RodRoomStatistics } from '~/stats';
import { Box, Icon, IconSize, useSideSheetState, Text } from '@bechtel/gds';
import { CloseBulkRequests } from './close/multi';
import { BulkReturnRodRequestCaddy } from './bulk-return';
//@ts-ignore needed for declaration generation
import { ReactComponent as RodRequestEmptyStateIcon } from '@bechtel/client-assets/dist/images/empty-weld-detail.svg';
import { Trans } from '@lingui/macro';
import { useLazyQuery } from '@apollo/client';
import { RodRoomStats, RodRoomStatsInput } from '@bechtel/types';
import { ROD_ROOM_STATS_SUMMARY } from '@bechtel/client-gql';
import { get } from 'lodash';
import { useRodRoomPermissions } from '@bechtel/client-weld-common';
import { EmulateAction, useAppUser, useEnvProvider } from '@bechtel/client-common';
import { RodRoomDataContext } from '~/utilities/context';

export const RodRequests = () => {
  const { envConfig } = useEnvProvider();
  const { visible, open, close } = useSideSheetState(false);
  const { user } = useAppUser();
  const enableEmulate = envConfig.enableEmulation.toLowerCase() === 'true' || user?.canEmulate;
  const { visible: bulkReturnVisible, open: bulkReturnOpen, close: bulkReturnClose } = useSideSheetState(false);
  const { selectedRodRoom } = useContext(RodRoomDataContext);
  const [loadRodRoomStats, { data: statsData }] = useLazyQuery<
    { rodRoomStatsSummary: RodRoomStats },
    { input: RodRoomStatsInput }
  >(ROD_ROOM_STATS_SUMMARY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  const rodRequestStatsSummary = get(statsData, 'rodRoomStatsSummary');
  const { canReturnCaddies, canCloseRodRequest } = useRodRoomPermissions();

  useEffect(() => {
    loadRodRoomStats({
      variables: {
        input: {
          rodRoomId: selectedRodRoom?.id,
        },
      },
    });
  }, [selectedRodRoom?.id]);

  return (
    <>
      <Box>
        <ErrorBoundary>
          <RodRoomRequestList rodRequestStats={rodRequestStatsSummary?.rodRequestStats} />
        </ErrorBoundary>
        <ErrorBoundary>
          <RodRequestDetailContainerStyled>
            <StatContainerStyled>
              <RodRoomStatistics rodRoomCaddyStat={rodRequestStatsSummary?.caddyStats} />
              <Box>
                {enableEmulate && (
                  <Box alignItems={{ sm: 'center' }}>
                    <EmulateAction />
                  </Box>
                )}
                <IconContainerStyled>
                  {canCloseRodRequest && (
                    <Box
                      css={{ cursor: 'pointer', width: '44px' }}
                      mr={{ sm: '400' }}
                      onClick={() => {
                        open();
                      }}
                    >
                      <Icon icon="check-double" size={IconSize.MEDIUM} fillColor="green.500" />
                    </Box>
                  )}
                  {canReturnCaddies && (
                    <Box
                      css={{ cursor: 'pointer', width: '44px' }}
                      mr={{ sm: '400' }}
                      onClick={() => {
                        bulkReturnOpen();
                      }}
                    >
                      <Icon icon="repeat" size={IconSize.MEDIUM} fillColor="bechtel.blue.700" />
                    </Box>
                  )}
                  <Icon
                    style={{ pointerEvents: 'none' }}
                    icon="user-cog"
                    size={IconSize.MEDIUM}
                    fillColor="bechtel.blue.700"
                  />
                </IconContainerStyled>
              </Box>
            </StatContainerStyled>
            <Routes>
              <Route path={`:id`} element={<RodRequestDetail />} />
              <Route
                path="/"
                element={
                  <Box
                    flexDirection={{ sm: 'column' }}
                    alignItems={{ sm: 'center' }}
                    justifyContent={{ sm: 'center' }}
                    style={{ height: '100%' }}
                    css={`
                      background-color: ${({ theme }) => theme.colors.white};
                    `}
                  >
                    <RodRequestEmptyStateIcon />
                    <Box mt={{ sm: '700' }} px={{ sm: '600' }}>
                      <Text family="graphik" color="grey.600">
                        <Trans>Select a rod request</Trans>
                      </Text>
                    </Box>
                  </Box>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </RodRequestDetailContainerStyled>
        </ErrorBoundary>
      </Box>
      {visible && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <CloseBulkRequests onClose={close} />
          </Suspense>
        </ErrorBoundary>
      )}
      {bulkReturnVisible && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <BulkReturnRodRequestCaddy onClose={bulkReturnClose} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
